/** Types voor de banktransacties-module — spiegel van de backend-shapes. */

export type BankMatchStatus = "UNMATCHED" | "SUGGESTED" | "MATCHED" | "IGNORED";
export type BankSource = "RABOBANK" | "ING" | "ABN_AMRO" | "GENERIC";
export type BankImportType = "CSV" | "PDF";
export type BankImportStatus = "PARSING" | "COMPLETED" | "FAILED";
export type MatchTargetType = "INVOICE" | "EXPENSE" | "CREATE_PAYMENT" | "CREATE_EXPENSE" | "BOOK_INTERNAL";

export interface MatchSuggestion {
  targetType: MatchTargetType;
  targetId?: string;
  score: number;
  confidence: number;
  reasons: string[];
  invoice?: { id: string; number: string; contactName: string | null; openEur: number };
  expense?: { id: string; description: string; merchant: string | null };
}

export interface BankTransaction {
  id: string;
  date: string;
  counterparty?: string | null;
  description: string;
  amount: number;
  currency: string;
  amountEur: number;
  balanceAfter?: number | null;
  rawReference?: string | null;
  matchStatus: BankMatchStatus;
  matchConfidence?: number | null;
  matchRule?: string | null;
  matchNote?: string | null;
  matchedAt?: string | null;
  invoice?: { id: string; number: string; totalEur: number; status: string; contact?: { companyName: string } } | null;
  payment?: { id: string; amountEur: number } | null;
  expense?: { id: string; description: string; amountEurInclVat: number } | null;
  sourceImport?: { id: string; fileName: string } | null;
  createdAt: string;
}

export interface BankImport {
  id: string;
  fileName: string;
  fileType: BankImportType;
  bankSource: BankSource;
  status: BankImportStatus;
  errorMessage?: string | null;
  transactionCount: number;
  duplicateCount: number;
  parseReport?: {
    detectedColumns?: Record<string, string>;
    warnings?: string[];
    pageCount?: number | null;
    duplicatesSkipped?: { date: string; amount: number; counterparty: string | null; description: string; reason: "exact" | "near" }[] | null;
  } | null;
  createdAt: string;
}

export interface PeriodLock {
  id: string;
  year: number;
  month: number;
  closedAt: string;
  note?: string | null;
}

export interface MonthSummary {
  year: number;
  month: number;
  inEur: number;
  uitEur: number;
  unmatched: number;
  suggested: number;
  matched: number;
  ignored: number;
  locked: boolean;
}

export interface Anomaly {
  kind: "AMOUNT_DEVIATION" | "DUPLICATE_SUSPECT" | "UNMATCHED_OLD" | "PAID_NOT_CLOSED";
  severity: "HOOG" | "MIDDEL" | "LAAG";
  message: string;
  bankTransactionId?: string;
  referenceId?: string;
  referenceLabel?: string;
  ageDays?: number;
  aging?: "Monitoren" | "Onderzoeken" | "Escaleren" | "Stagnerend";
}

export interface MissingReceipt {
  expenseId: string;
  date: string;
  merchant?: string;
  description: string;
  amountEur: number;
  viaBank: boolean;
  hint?: string;
}

export interface ProfitLossResult {
  months: { year: number; month: number; omzetExcl: number; kostenExcl: number; winst: number }[];
  totals: { omzetExcl: number; kostenExcl: number; winst: number; btwOmzet: number; btwKosten: number };
}

export interface CashflowResult {
  months: { year: number; month: number; inEur: number; uitEur: number; netto: number; nietGeboektIn: number; nietGeboektUit: number; eindsaldo: number | null }[];
  totals: { inEur: number; uitEur: number; netto: number };
}

export interface VatSummary {
  periodType: "MONTHLY" | "QUARTERLY" | "YEARLY";
  sales: { percentage: number; basis: number; btw: number }[];
  purchases: { percentage: number; basis: number; btw: number }[];
  omzetBtw: number;
  aankoopBtw: number;
  teBetalen: number;
}

export interface BalanceResult {
  debiteuren: { id: string; number: string; contactName: string | null; dueDate: string; openEur: number }[];
  crediteuren: { id: string; description: string; merchant: string | null; date: string; amountEur: number }[];
  bankSaldo: number | null;
  btwSaldo: number;
  totalOpenDebiteuren: number;
  totalOpenCrediteuren: number;
}

// ── Gecombineerd totaalplaatje (Dashboard → Rapporten) ───────────────────────

export type OverviewGranularity = "year" | "quarter" | "month" | "day";
export type OverviewSource = "facturen" | "uitgaven" | "bank" | "kasboek";

export interface OverviewBucket {
  key: string;
  label: string;
  year: number;
  quarter?: number;
  month?: number;
  day?: number;
  bankIn: number;
  bankUit: number;
  kasIn: number;
  kasUit: number;
  betalingenIn: number;
  uitgavenUit: number;
  factuurOmzet: number;
  kostenUitgaven: number;
  kasOmzet: number;
  kasKosten: number;
  geldstroomIn: number;
  geldstroomUit: number;
  nettoGeldstroom: number;
  nettoBoekhoudkundig: number;
  eindsaldoGeldstroom: number | null;
}

export interface OverviewVerdeling {
  source: OverviewSource;
  in: number;
  uit: number;
  netto: number;
}

export interface OverviewResult {
  year: number;
  granularity: OverviewGranularity;
  sources: OverviewSource[];
  openingSaldoGeldstroom: { bank: number | null; kas: number };
  buckets: OverviewBucket[];
  totals: Omit<OverviewBucket, "key" | "label" | "year" | "quarter" | "month" | "day" | "eindsaldoGeldstroom">;
  verdelingPerBron: OverviewVerdeling[];
}

export const matchStatusLabels: Record<BankMatchStatus, string> = {
  UNMATCHED: "Niet gekoppeld",
  SUGGESTED: "Voorstel",
  MATCHED: "Gekoppeld",
  IGNORED: "Genegeerd",
};

// ── Banksync (GoCardless) ────────────────────────────────────────────────────

export type BankAccountStatus = "ACTIVE" | "CONSENT_EXPIRED" | "REVOKED";

export interface BankAccount {
  id: string;
  provider: string;
  institutionId: string;
  institutionName: string;
  iban?: string | null;
  ownerName?: string | null;
  accountName?: string | null;
  currency: string;
  status: BankAccountStatus;
  accessValidUntil?: string | null;
  lastSyncAt?: string | null;
  lastError?: string | null;
  lastSyncResult?: { fetched: number; pending: number; inserted: number; duplicates: number; skipped: number; autoMatched: number } | null;
  createdAt: string;
}

export interface GcInstitution {
  id: string;
  name: string;
  bic?: string | null;
  logo?: string | null;
  maxAccessValidForDays: number;
  transactionTotalDays: number;
}

export interface SyncResult {
  fetched: number;
  pending: number;
  inserted: number;
  duplicates: number;
  skipped: number;
  autoMatched: number;
  status: BankAccountStatus;
}

export const bankAccountStatusLabels: Record<BankAccountStatus, string> = {
  ACTIVE: "Actief",
  CONSENT_EXPIRED: "Toestemming verlopen",
  REVOKED: "Ingetrokken",
};